import type { Team, TeamRegistrationIssue, TeamRegistrationSummary } from '../store/api/teamsApi'

const REQUIRED_MEMBER_COUNT = 4

function issue(
  key: string,
  label: string,
  message: string,
  severity: TeamRegistrationIssue['severity'] = 'error',
  blocker = true,
): TeamRegistrationIssue {
  return { key, label, message, severity, blocker }
}

export function getTeamRegistrationSummary(team: Team): TeamRegistrationSummary {
  if (team.registration_summary) return team.registration_summary

  const members = team.members || []
  const memberCards = team.member_cards || {}
  const memberDetails = team.member_details || {}
  const issues: TeamRegistrationIssue[] = []

  if (members.length !== REQUIRED_MEMBER_COUNT) {
    issues.push(issue(
      'member_count',
      'Chưa đủ thành viên',
      `Đội phải có đúng ${REQUIRED_MEMBER_COUNT} thành viên. Hiện tại: ${members.length}.`,
    ))
  }

  if (new Set(members).size !== members.length) {
    issues.push(issue('duplicate_members', 'Trùng thành viên', 'Danh sách đội có thành viên bị trùng.'))
  }

  const missingCards = members.filter((memberId) => !memberCards[memberId])
  if (missingCards.length > 0) {
    issues.push(issue('member_cards', 'Thiếu thẻ sinh viên', `Còn ${missingCards.length} thành viên chưa upload thẻ SV.`))
  }

  let missingStudentId = 0
  const missingDetails: string[] = []
  const fields: Array<[string, string]> = [
    ['student_id', 'MSSV'],
    ['full_name', 'Họ tên'],
    ['date_of_birth', 'Ngày sinh'],
    ['university', 'Trường'],
    ['faculty', 'Khoa'],
    ['email', 'Email'],
  ]

  members.forEach((memberId, index) => {
    const details = memberDetails[memberId] || {}
    fields.forEach(([field, label]) => {
      if (!String(details[field as keyof typeof details] || '').trim()) {
        if (field === 'student_id') missingStudentId += 1
        missingDetails.push(`TV${index + 1}: ${label}`)
      }
    })
  })

  if (missingStudentId > 0) {
    issues.push(issue('missing_student_id', 'Thiếu MSSV', `Còn ${missingStudentId} thành viên thiếu MSSV.`))
  }

  const missingOtherDetails = missingDetails.filter((item) => !item.endsWith(': MSSV'))
  if (missingOtherDetails.length > 0) {
    const preview = missingOtherDetails.slice(0, 6).join(', ')
    issues.push(issue('member_details', 'Thiếu thông tin thành viên', `Cần bổ sung ${preview}${missingOtherDetails.length > 6 ? '...' : ''}.`))
  }

  if (!team.profile_file) {
    issues.push(issue('team_profile', 'Thiếu hồ sơ đội', 'Chưa upload file Team Profile.'))
  }

  if (!team.intro_video) {
    issues.push(issue('intro_video', 'Thiếu video giới thiệu', 'Chưa upload video giới thiệu đội.'))
  }

  if (team.status === 'rejected') {
    issues.push(issue('rejected', 'Hồ sơ bị từ chối', team.rejection_reason || 'Cần chỉnh sửa theo lý do từ BTC.', 'warning'))
  }

  const requiredKeys = new Set([
    'member_count',
    'duplicate_members',
    'member_cards',
    'missing_student_id',
    'member_details',
    'team_profile',
    'intro_video',
  ])
  const missingRequired = new Set(issues.filter((item) => item.blocker && requiredKeys.has(item.key)).map((item) => item.key))
  const requiredTotal = requiredKeys.size

  return {
    is_complete: !issues.some((item) => item.blocker),
    required_done: Math.max(0, requiredTotal - missingRequired.size),
    required_total: requiredTotal,
    missing_count: issues.filter((item) => item.blocker).length,
    missing_keys: issues.map((item) => item.key),
    has_profile_file: Boolean(team.profile_file),
    has_intro_video: Boolean(team.intro_video),
    issues,
  }
}

export function getTeamRegistrationLabel(team: Team): string {
  const summary = getTeamRegistrationSummary(team)
  if (team.status === 'active' && summary.is_complete) return 'Đã duyệt'
  if (team.status === 'rejected') return 'Cần sửa hồ sơ'
  if (summary.is_complete) return 'Đủ hồ sơ'
  return `Thiếu ${summary.missing_count} mục`
}

export function getTeamRegistrationColor(team: Team): 'success' | 'warning' | 'error' | 'info' | 'default' {
  const summary = getTeamRegistrationSummary(team)
  if (team.status === 'rejected') return 'error'
  if (team.status === 'active' && summary.is_complete) return 'success'
  if (summary.is_complete) return 'info'
  return 'warning'
}

export function getPriorityRegistrationIssues(team: Team, limit = 4): TeamRegistrationIssue[] {
  return getTeamRegistrationSummary(team).issues.slice(0, limit)
}
